import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Package, TrendingUp, Truck, BarChart3, RefreshCcw, ShoppingCart } from 'lucide-react';

const ZohoOrderManagement = () => {
  const features = [
    {
      icon: <ShoppingCart className="w-6 h-6 text-blue-700" />,
      title: 'Order Processing',
      description: 'Efficiënt beheer van bestellingen van ontvangst tot levering.',
    },
    {
      icon: <Package className="w-6 h-6 text-blue-700" />,
      title: 'Voorraadbeheer',
      description: 'Real-time inzicht in voorraad over alle locaties.',
    },
    {
      icon: <Truck className="w-6 h-6 text-blue-700" />,
      title: 'Verzending & Tracking',
      description: 'Integratie met verzenders en real-time track & trace.',
    },
    {
      icon: <RefreshCcw className="w-6 h-6 text-blue-700" />,
      title: 'Retourenbeheer',
      description: 'Stroomlijn retourprocessen en voorraad bijwerking.',
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-blue-700" />,
      title: 'Analytics',
      description: 'Inzicht in order trends en fulfillment prestaties.',
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-700" />,
      title: 'Multi-channel',
      description: 'Beheer orders van webshop, marktplaatsen en retail.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                Order Management Systeem
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho Order Management: Stroomlijn Uw Orderverwerking
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Automatiseer uw volledige orderverwerkingsproces met Zoho Order Management. Van bestelling tot 
                levering - alles in één geïntegreerd systeem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxkYXNoYm9hcmR8ZW58MHx8fHwxNzczODE3NjA4fDA&ixlib=rb-4.1.0&q=85"
                alt="Order Management Dashboard"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho Order Management Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho Order Management?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho Order Management is een complete oplossing voor het beheren van bestellingen, voorraad en 
                verzending. Het systeem integreert naadloos met webshops, marktplaatsen en andere verkoopkanalen 
                om alle orders centraal te verwerken.
              </p>
              <p>
                Als Zoho Partner helpt Guijs Consultancy u met de implementatie van Zoho Order Management dat 
                aansluit op uw fulfillment processen. Wij zorgen voor integraties met uw webshop, ERP systeem, 
                verzenders en andere belangrijke systemen.
              </p>
              <p>
                Of u nu B2B of B2C orders verwerkt, met Zoho Order Management krijgt u volledige controle over 
                uw orderverwerking en kunt u uw klanten een uitstekende service bieden met real-time updates 
                en snelle levering.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho Order Management
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Van order tot levering - automatiseer uw gehele fulfillment proces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho Order Management
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho Order Management versnelt uw orderverwerking en verhoogt klanttevredenheid.
              </p>

              <ul className="space-y-4">
                {[
                  'Verwerk orders sneller met geautomatiseerde workflows',
                  'Verminder fouten in orderverwerking en verzending',
                  'Krijg real-time inzicht in voorraad en orders',
                  'Verbeter klanttevredenheid met snellere levering',
                  'Schaal mee van 10 tot 10.000 orders per dag',
                  'Integreer met alle belangrijke webshop platforms',
                ].map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <img
                src="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxkYXNoYm9hcmR8ZW58MHx8fHwxNzczODE3NjA4fDA&ixlib=rb-4.1.0&q=85"
                alt="Order Processing"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Zoho Order Management?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij zorgen voor een naadloze implementatie die uw fulfillment proces optimaliseert.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Webshop Integraties</h3>
                  <p className="text-gray-600">
                    Koppeling met Shopify, WooCommerce, Magento en andere webshop platforms.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Verzender Koppelingen</h3>
                  <p className="text-gray-600">
                    Integratie met PostNL, DHL, DPD en andere verzenders voor automatische labeling.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Workflow Automatisering</h3>
                  <p className="text-gray-600">
                    Automatische orderverwerking, voorraad updates en klantvriendelijke communicatie.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Training & Optimalisatie</h3>
                  <p className="text-gray-600">
                    Continue verbetering van uw fulfillment proces voor maximale efficiency.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar voor Efficiënte Orderverwerking?"
        subtitle="Ontdek hoe Zoho Order Management uw fulfillment kan optimaliseren."
      />
    </div>
  );
};

export default ZohoOrderManagement;
